import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { z } from "zod";

function publicClient() {
  return createClient(
    process.env.SUPABASE_URL!,
    process.env.SUPABASE_PUBLISHABLE_KEY!,
    { auth: { persistSession: false } },
  );
}

export type BlogPostSummary = {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  cover_image_url: string | null;
  published_at: string | null;
  minutes_to_read: number | null;
};

export type BlogPost = BlogPostSummary & {
  content_html: string;
  author: string | null;
  tags: string[];
};

export const listPublishedPosts = createServerFn({ method: "GET" }).handler(
  async (): Promise<BlogPostSummary[]> => {
    const sb = publicClient();
    const { data, error } = await sb
      .from("blog_posts")
      .select("id,slug,title,excerpt,cover_image_url,published_at,minutes_to_read")
      .eq("status", "published")
      .order("published_at", { ascending: false });
    if (error) throw new Error(error.message);
    return (data ?? []) as BlogPostSummary[];
  },
);

export const getPostBySlug = createServerFn({ method: "GET" })
  .inputValidator((i: unknown) => z.object({ slug: z.string() }).parse(i))
  .handler(async ({ data }): Promise<BlogPost | null> => {
    const sb = publicClient();
    const { data: row, error } = await sb
      .from("blog_posts")
      .select(
        "id,slug,title,excerpt,cover_image_url,published_at,minutes_to_read,content_html,author,tags",
      )
      .eq("slug", data.slug)
      .eq("status", "published")
      .maybeSingle();
    if (error) throw new Error(error.message);
    return (row as BlogPost | null) ?? null;
  });

export const getBlogPageContent = createServerFn({ method: "GET" }).handler(
  async () => {
    const sb = publicClient();
    const { data, error } = await sb
      .from("blog_content")
      .select("hero_title,hero_subtitle,seo_title,seo_description")
      .eq("id", 1)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return (
      data ?? {
        hero_title: "Stories from Backland",
        hero_subtitle: "Field notes, guides, and inspiration from Northern Arizona.",
        seo_title: null,
        seo_description: null,
      }
    );
  },
);

import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export type AdminBlogPost = {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  content_html: string;
  cover_image_url: string | null;
  author: string | null;
  tags: string[];
  status: string;
  published_at: string | null;
  minutes_to_read: number | null;
  updated_at: string;
};

async function assertAdmin(supabase: any, userId: string) {
  const { data, error } = await supabase.rpc("has_role", { _user_id: userId, _role: "admin" });
  if (error) throw new Error(error.message);
  if (!data) throw new Error("Forbidden: admin access required");
}

export const adminListPosts = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<AdminBlogPost[]> => {
    await assertAdmin(context.supabase, context.userId);
    const { data, error } = await context.supabase
      .from("blog_posts")
      .select("id,slug,title,excerpt,content_html,cover_image_url,author,tags,status,published_at,minutes_to_read,updated_at")
      .order("published_at", { ascending: false, nullsFirst: false });
    if (error) throw new Error(error.message);
    return (data ?? []) as AdminBlogPost[];
  });

export const adminGetPost = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => z.object({ id: z.string().uuid() }).parse(i))
  .handler(async ({ context, data }): Promise<AdminBlogPost | null> => {
    await assertAdmin(context.supabase, context.userId);
    const { data: row, error } = await context.supabase
      .from("blog_posts")
      .select("id,slug,title,excerpt,content_html,cover_image_url,author,tags,status,published_at,minutes_to_read,updated_at")
      .eq("id", data.id)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return (row as AdminBlogPost | null) ?? null;
  });

const upsertPostSchema = z.object({
  id: z.string().uuid().optional(),
  slug: z.string().min(1).max(200).regex(/^[a-z0-9-]+$/, "lowercase letters, numbers, hyphens"),
  title: z.string().min(1).max(300),
  excerpt: z.string().max(1000).nullable(),
  content_html: z.string().max(500000),
  cover_image_url: z.string().url().nullable(),
  author: z.string().max(200).nullable(),
  tags: z.array(z.string().max(50)).max(30),
  status: z.enum(["draft", "published"]),
  published_at: z.string().nullable(),
  minutes_to_read: z.number().int().min(0).max(999).nullable(),
});

export const adminUpsertPost = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => upsertPostSchema.parse(i))
  .handler(async ({ context, data }) => {
    await assertAdmin(context.supabase, context.userId);
    const row = {
      ...data,
      locally_edited: true,
      updated_at: new Date().toISOString(),
    };
    if (data.id) {
      const { error } = await context.supabase.from("blog_posts").update(row).eq("id", data.id);
      if (error) throw new Error(error.message);
      return { id: data.id };
    }
    const { data: ins, error } = await context.supabase
      .from("blog_posts")
      .insert(row)
      .select("id")
      .single();
    if (error) throw new Error(error.message);
    return { id: ins.id as string };
  });

export const adminDeletePost = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => z.object({ id: z.string().uuid() }).parse(i))
  .handler(async ({ context, data }) => {
    await assertAdmin(context.supabase, context.userId);
    const { error } = await context.supabase.from("blog_posts").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const adminUpdateBlogContent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z.object({
      hero_title: z.string().min(1).max(200),
      hero_subtitle: z.string().max(500).nullable(),
      seo_title: z.string().max(200).nullable(),
      seo_description: z.string().max(500).nullable(),
    }).parse(i),
  )
  .handler(async ({ context, data }) => {
    await assertAdmin(context.supabase, context.userId);
    const { error } = await context.supabase
      .from("blog_content")
      .upsert({ id: 1, ...data, updated_at: new Date().toISOString() }, { onConflict: "id" });
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// ---------------------------------------------------------------------------
// One-time restore: fetch missing list content from the linked Wix Blog and
// splice it back into blog_posts.content_html. Also strips duplicate leading
// cover images. Idempotent.
// ---------------------------------------------------------------------------

const WIX_SITE_ID = "482caf9b-85ad-4682-af7c-dc3e14d7926e";
const WIX_GATEWAY = "https://connector-gateway.lovable.dev/wix";

async function fetchWixPostBySlug(slug: string): Promise<{ nodes?: unknown[] } | null> {
  const lovableKey = process.env.LOVABLE_API_KEY;
  const wixKey = process.env.WIX_API_KEY;
  if (!lovableKey || !wixKey) throw new Error("Missing LOVABLE_API_KEY or WIX_API_KEY");
  const url = `${WIX_GATEWAY}/blog/v3/posts/slugs/${encodeURIComponent(slug)}?fieldsets=RICH_CONTENT`;
  const res = await fetch(url, {
    headers: {
      Authorization: `Bearer ${lovableKey}`,
      "X-Connection-Api-Key": wixKey,
      "wix-site-id": WIX_SITE_ID,
    },
  });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`Wix ${res.status}: ${await res.text()}`);
  const body = (await res.json()) as { post?: { richContent?: { nodes?: unknown[] } } };
  return body.post?.richContent ?? null;
}

type RestoreReport = {
  slug: string;
  status: "restored" | "cover-only" | "skipped" | "error";
  message: string;
  listsRestored?: number;
};

export const adminRestoreWixLists = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<RestoreReport[]> => {
    await assertAdmin(context.supabase, context.userId);
    const { extractListsHtml, spliceEmptyLists, stripLeadingCoverImage } = await import(
      "./wix-ricos"
    );

    const { data: posts, error } = await context.supabase
      .from("blog_posts")
      .select("id,slug,content_html,cover_image_url")
      .order("slug");
    if (error) throw new Error(error.message);

    const reports: RestoreReport[] = [];
    for (const p of (posts ?? []) as Array<{
      id: string;
      slug: string;
      content_html: string;
      cover_image_url: string | null;
    }>) {
      const hasEmptyLists = /<(ul|ol)\b[^>]*>\s*(?:<li\b[^>]*>\s*<\/li>\s*)+<\/\1>/i.test(
        p.content_html,
      );
      let nextHtml = stripLeadingCoverImage(p.content_html, p.cover_image_url);
      let listsRestored = 0;
      const coverChanged = nextHtml !== p.content_html;

      if (hasEmptyLists) {
        let rc: { nodes?: unknown[] } | null;
        try {
          rc = await fetchWixPostBySlug(p.slug);
        } catch (e) {
          reports.push({
            slug: p.slug,
            status: "error",
            message: e instanceof Error ? e.message : String(e),
          });
          continue;
        }
        if (!rc) {
          reports.push({ slug: p.slug, status: "skipped", message: "Wix returned 404" });
          continue;
        }
        const lists = extractListsHtml(rc as any);
        const result = spliceEmptyLists(nextHtml, lists);
        if ("error" in result) {
          reports.push({
            slug: p.slug,
            status: "skipped",
            message: `count mismatch: ${result.emptyCount} empty lists in DB vs ${result.wixCount} lists in Wix`,
          });
          continue;
        }
        nextHtml = result.html;
        listsRestored = result.replaced;
      }

      if (listsRestored === 0 && !coverChanged) {
        continue; // nothing to do
      }

      const { error: uErr } = await context.supabase
        .from("blog_posts")
        .update({ content_html: nextHtml, updated_at: new Date().toISOString() })
        .eq("id", p.id);
      if (uErr) {
        reports.push({ slug: p.slug, status: "error", message: uErr.message });
        continue;
      }
      reports.push({
        slug: p.slug,
        status: listsRestored > 0 ? "restored" : "cover-only",
        message: coverChanged
          ? `${listsRestored} lists, stripped leading cover image`
          : `${listsRestored} lists`,
        listsRestored,
      });
    }
    return reports;
  });
